import React from 'react';
import { Team, Campaign } from '../types';
import { Users, CheckCircle2, Circle, Megaphone, AlertTriangle } from 'lucide-react';
interface TeamSelectorProps {
  teams: Team[];
  campaigns: Campaign[];
  selectedTeamIds: string[];
  teamCampaignMap: Record<string, string>;
  onToggleTeam: (teamId: string) => void;
  onSetCampaign: (teamId: string, campaignId: string) => void;
  perTeamCount?: number | null;
}
export function TeamSelector({
  teams,
  campaigns,
  selectedTeamIds,
  teamCampaignMap,
  onToggleTeam,
  onSetCampaign,
  perTeamCount
}: TeamSelectorProps) {
  const activeCampaigns = campaigns.filter(c => c.status === 'active');
  return <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-148">
      <div className="px-6 py-5 border-b border-slate-200 bg-slate-50/50" data-id="element-149">
        <h2 className="text-lg font-semibold text-slate-900" data-id="element-150">
          2. Select Teams & Campaigns
        </h2>
        <p className="text-sm text-slate-500 mt-1" data-id="element-151">
          Choose which teams will receive leads, and the Bonzo campaign each
          team's leads will enter.
        </p>
      </div>
      <div className="p-6" data-id="element-152">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-153">
          {teams.map(team => {
          const isSelected = selectedTeamIds.includes(team.id);
          const campaignId = teamCampaignMap[team.id] || '';
          const needsCampaign = isSelected && !campaignId;
          return <div key={team.id} className={`relative p-4 rounded-xl border-2 transition-all duration-200 ${isSelected ? needsCampaign ? 'border-amber-400 bg-amber-50/30' : 'border-indigo-600 bg-indigo-50/30' : 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50 cursor-pointer'}`} onClick={e => {
            if (!isSelected) onToggleTeam(team.id);
          }} data-id="element-154">
                <div className={`flex justify-between items-start mb-3 ${isSelected ? 'cursor-pointer' : ''}`} onClick={() => isSelected && onToggleTeam(team.id)} data-id="element-155">
                  <div className={`p-2 rounded-lg ${team.bgClass} ${team.colorClass}`} data-id="element-156">
                    <Users className="w-5 h-5" data-id="element-157" />
                  </div>
                  {isSelected ? <CheckCircle2 className="w-5 h-5 text-indigo-600" data-id="element-158" /> : <Circle className="w-5 h-5 text-slate-300" data-id="element-159" />}
                </div>
                <h3 className="font-medium text-slate-900" data-id="element-160">{team.name}</h3>
                <p className="text-xs text-slate-500 mt-1 line-clamp-1" data-id="element-161">
                  {team.description}
                </p>
                <div className="mt-3 flex items-center justify-between" data-id="element-162">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded-md" data-id="element-163">
                    {team.memberCount} members
                  </div>
                  {isSelected && perTeamCount != null && <div className="text-xs font-semibold text-indigo-600 tabular-nums" data-id="element-164">
                      ~{perTeamCount.toLocaleString()} leads
                    </div>}
                </div>

                {/* Campaign picker (only when team is selected) */}
                {isSelected && <div className="mt-3 pt-3 border-t border-slate-200" data-id="element-165">
                    <label className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5" data-id="element-166">
                      <Megaphone className="w-3 h-3" data-id="element-167" />
                      Bonzo Campaign
                    </label>
                    <select value={campaignId} onChange={e => onSetCampaign(team.id, e.target.value)} onClick={e => e.stopPropagation()} className={`w-full px-2 py-1.5 rounded-md border text-xs bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 ${needsCampaign ? 'border-amber-400 text-amber-900' : 'border-slate-200 text-slate-800'}`} data-id="element-168">
                      <option value="" data-id="element-169">Select a campaign…</option>
                      {activeCampaigns.map(c => <option key={c.id} value={c.id} data-id="element-170">
                          {c.name}
                        </option>)}
                    </select>
                    {needsCampaign && <div className="flex items-center gap-1 mt-1.5 text-[11px] text-amber-700" data-id="element-171">
                        <AlertTriangle className="w-3 h-3" data-id="element-172" />
                        Campaign required
                      </div>}
                  </div>}
              </div>;
        })}
        </div>
      </div>
    </div>;
}